import {
  BoxSize,
  CarrierService,
  ItemType,
  OrderType,
  PackOrder,
  PackQueueItem,
  PackSession,
} from './types';

const deriveOrderType = (itemTypes: ItemType[]): OrderType => {
  // Aligned to server FulfillmentService.deriveOrderType (frames/wheels are their own category).
  const hasBike = itemTypes.some(t => t === 'bike');
  const hasFrameOrWheel = itemTypes.some(t => t === 'frame' || t === 'wheel');
  const hasPaa = itemTypes.some(t => t === 'paa');
  const categories = [hasBike, hasFrameOrWheel, hasPaa].filter(Boolean).length;
  if (categories === 0) return 'unknown';
  if (categories > 1) return 'mixed';
  if (hasBike) return 'bike_only';
  if (hasFrameOrWheel) return 'wheels_frames_only';
  return 'paa_only';
};

// Placeholder — will be loaded from DB when implemented (dimensions: L×W×H inches)
export const BOX_SIZES: { size: BoxSize; tareLbs: number; dims: string }[] = [
  { size: 'Bike Box - XXL', tareLbs: 30, dims: '69×10×34' },
  { size: 'Bike Box - Stranger', tareLbs: 28, dims: '55×20×32' },
  { size: 'Bike Box - Medium', tareLbs: 28, dims: '60×10×33' },
  { size: 'Bike Box - Small', tareLbs: 25, dims: '54×8×28' },
  { size: 'Frame Box', tareLbs: 10, dims: '43×8×26' },
  { size: 'Wheel Box - Double', tareLbs: 8, dims: '32×6×27' },
  { size: 'Wheel Box - Single', tareLbs: 5, dims: '26×6×26' },
  { size: 'Saddle Box', tareLbs: 5, dims: '14×6×6' },
  { size: 'Crank Box', tareLbs: 5, dims: '12×9×6' },
  { size: 'Poly Bag', tareLbs: 3, dims: '12×9×0.25' },
  { size: 'Small P&A Box', tareLbs: 2, dims: '8×6×5' },
];

export const MOCK_ORDERS: PackOrder[] = [
  {
    shopifyOrderId: 8006,
    shopifyOrderName: '#TPC-8006',
    shopifyOrderGid: 'gid://shopify/Order/8006',
    createdAt: '2026-04-20T08:00:00Z',
    lifecycleStatus: 'picked',
    isPriority: false,
    customer: { firstName: 'Chris', lastName: 'Froome', email: 'cfroome@example.com' },
    shippingAddress: {
      address1: '42 Alpe Ave',
      address2: null,
      city: 'Boulder',
      province: 'CO',
      zip: '80301',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80061',
        sku: 'BIK-TREK-MADONE-SL5-54-BLK',
        title: 'Trek Madone SL 5 — 54cm / Matte Black',
        imageUrl: null,
        weightLbs: 18.5,
        upc: '060115863001',
        mpn: 'MADONE-SL5-54-BLK-2023',
        itemType: 'bike',
        binLocations: [
          {
            id: 101,
            binCode: 'BIKE-A12',
            quantity: 1,
            zone: 'Bike Floor',
            parentZone: null,
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: false,
  },
  {
    shopifyOrderId: 8007,
    shopifyOrderName: '#TPC-8007',
    shopifyOrderGid: 'gid://shopify/Order/8007',
    createdAt: '2026-04-20T09:30:00Z',
    lifecycleStatus: 'picked',
    isPriority: true,
    customer: { firstName: 'Tadej', lastName: 'Pogacar', email: 'tpog@example.com' },
    shippingAddress: {
      address1: '1 Strade Bianche',
      address2: null,
      city: 'Denver',
      province: 'CO',
      zip: '80202',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80071',
        sku: 'BIK-TREK-DOMANE-SL6-56-WHT',
        title: 'Trek Domane SL 6 — 56cm / White',
        imageUrl: null,
        weightLbs: 21.0,
        upc: '060115864001',
        mpn: 'DOMANE-SL6-56-WHT-2023',
        itemType: 'bike',
        binLocations: [
          {
            id: 102,
            binCode: 'BIKE-A08',
            quantity: 1,
            zone: 'Bike Floor',
            parentZone: null,
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80072',
        sku: 'PAA-PEDALS-SPD-SL-BLK',
        title: 'Shimano Ultegra PD-R8000 Pedals',
        imageUrl: null,
        weightLbs: 0.8,
        upc: '012345679001',
        mpn: 'PD-R8000',
        itemType: 'paa',
        binLocations: [
          {
            id: 201,
            binCode: 'C2-03-A',
            quantity: 2,
            zone: 'Shelving C',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80073',
        sku: 'PAA-TOOLKIT-MINI',
        title: "Pedro's Trixie Micro Tool Kit",
        imageUrl: null,
        weightLbs: 0.4,
        upc: '012345679002',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 202,
            binCode: 'D1-07-B',
            quantity: 4,
            zone: 'Shelving D',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: false,
  },
  {
    shopifyOrderId: 8001,
    shopifyOrderName: '#TPC-8001',
    shopifyOrderGid: 'gid://shopify/Order/8001',
    createdAt: '2026-04-20T10:00:00Z',
    lifecycleStatus: 'picked',
    isPriority: false,
    customer: { firstName: 'Alex', lastName: 'Chen', email: 'alex.chen@example.com' },
    shippingAddress: {
      address1: '123 Main St',
      address2: null,
      city: 'Denver',
      province: 'CO',
      zip: '80202',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80011',
        sku: 'PAA-HELMET-S-BLK',
        title: 'Giro Cinder MIPS Helmet — S / Black',
        imageUrl: null,
        weightLbs: 0.8,
        upc: '012345678901',
        mpn: 'CINDER-MIPS-S-BLK',
        itemType: 'paa',
        binLocations: [
          {
            id: 203,
            binCode: 'B3-02-C',
            quantity: 1,
            zone: 'Shelving B',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: false,
  },
  {
    shopifyOrderId: 8002,
    shopifyOrderName: '#TPC-8002',
    shopifyOrderGid: 'gid://shopify/Order/8002',
    createdAt: '2026-04-19T14:30:00Z',
    lifecycleStatus: 'picked',
    isPriority: true,
    customer: { firstName: 'Jordan', lastName: 'Mills', email: 'jmills@example.com' },
    shippingAddress: {
      address1: '456 Oak Ave',
      address2: 'Apt 3B',
      city: 'Boulder',
      province: 'CO',
      zip: '80301',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80021',
        sku: 'PAA-SHOES-42-WHT',
        title: 'Shimano SH-RC502 Road Shoes — 42 / White',
        imageUrl: null,
        weightLbs: 1.2,
        upc: '012345678902',
        mpn: 'SH-RC502-42-WHT',
        itemType: 'paa',
        binLocations: [
          {
            id: 204,
            binCode: 'E2-05-A',
            quantity: 1,
            zone: 'Shelving E',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
          {
            id: 205,
            binCode: 'E2-05-B',
            quantity: 1,
            zone: 'Shelving E',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80022',
        sku: 'PAA-BKSHORTS-M-BLK',
        title: 'Pearl Izumi Attack Bib Short — M / Black',
        imageUrl: null,
        weightLbs: 0.4,
        upc: '012345678903',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 206,
            binCode: 'F1-01-A',
            quantity: 2,
            zone: 'Shelving F',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80023',
        sku: 'PAA-GLOVES-L-BLK',
        title: 'Castelli Rosso Corsa Gloves — L / Black',
        imageUrl: null,
        weightLbs: 0.2,
        upc: '012345678904',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 207,
            binCode: 'F1-03-B',
            quantity: 3,
            zone: 'Shelving F',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: false,
  },
  {
    shopifyOrderId: 8003,
    shopifyOrderName: '#TPC-8003',
    shopifyOrderGid: 'gid://shopify/Order/8003',
    createdAt: '2026-04-18T09:15:00Z',
    lifecycleStatus: 'packing',
    isPriority: false,
    customer: { firstName: 'Sam', lastName: 'Rivera', email: 'srivera@example.com' },
    shippingAddress: {
      address1: '789 Pine Rd',
      address2: null,
      city: 'Colorado Springs',
      province: 'CO',
      zip: '80903',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80031',
        sku: 'PAA-PUMP-FLOOR-BLK',
        title: 'Topeak Joe Blow Sport Floor Pump',
        imageUrl: null,
        weightLbs: 2.1,
        upc: '012345678905',
        mpn: 'TJB-SPC',
        itemType: 'paa',
        binLocations: [
          {
            id: 208,
            binCode: 'A4-09-A',
            quantity: 2,
            zone: 'Shelving A',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80032',
        sku: 'PAA-CAGE-BLK',
        title: 'Elite Custom Race Bottle Cage — Black',
        imageUrl: null,
        weightLbs: 0.2,
        upc: '012345678906',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 209,
            binCode: 'B1-12-C',
            quantity: 8,
            zone: 'Shelving B',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80033',
        sku: 'PAA-LIGHT-FRONT',
        title: 'Bontrager Ion Pro RT Headlight',
        imageUrl: null,
        weightLbs: 0.3,
        upc: '012345678907',
        mpn: 'ION-PRO-RT',
        itemType: 'paa',
        binLocations: [
          {
            id: 210,
            binCode: 'B2-06-A',
            quantity: 3,
            zone: 'Shelving B',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80034',
        sku: 'PAA-LIGHT-REAR',
        title: 'Bontrager Flare RT Tail Light',
        imageUrl: null,
        weightLbs: 0.15,
        upc: '012345678908',
        mpn: 'FLARE-RT',
        itemType: 'paa',
        binLocations: [
          {
            id: 211,
            binCode: 'B2-06-B',
            quantity: 5,
            zone: 'Shelving B',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: false,
  },
  {
    shopifyOrderId: 8004,
    shopifyOrderName: '#TPC-8004',
    shopifyOrderGid: 'gid://shopify/Order/8004',
    createdAt: '2026-04-16T11:00:00Z',
    lifecycleStatus: 'picked',
    isPriority: false,
    customer: { firstName: 'Taylor', lastName: 'Kim', email: 'tkim@example.com' },
    shippingAddress: {
      address1: '99 Broken Ln',
      address2: null,
      city: 'Invalidcity',
      province: 'XX',
      zip: '00000',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80041',
        sku: 'PAA-JERSEY-M-BLU',
        title: 'Rapha Core Jersey — M / Blue',
        imageUrl: null,
        weightLbs: 0.35,
        upc: '012345678909',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 212,
            binCode: 'G3-04-A',
            quantity: 2,
            zone: 'Shelving G',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80042',
        sku: 'PAA-BIBTIGHTS-M-BLK',
        title: 'Rapha Pro Team Bib Tights — M / Black',
        imageUrl: null,
        weightLbs: 0.5,
        upc: '012345678910',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 213,
            binCode: 'G3-05-B',
            quantity: 1,
            zone: 'Shelving G',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: true,
  },
  {
    shopifyOrderId: 8005,
    shopifyOrderName: '#TPC-8005',
    shopifyOrderGid: 'gid://shopify/Order/8005',
    createdAt: '2026-04-17T16:45:00Z',
    lifecycleStatus: 'picked',
    isPriority: false,
    customer: { firstName: 'Morgan', lastName: 'Park', email: 'mpark@example.com' },
    shippingAddress: {
      address1: '321 Elm St',
      address2: 'Unit 5',
      city: 'Fort Collins',
      province: 'CO',
      zip: '80521',
      country: 'US',
    },
    lines: [
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80051',
        sku: 'PAA-SADDLE-RACE',
        title: 'Fizik Ares R3 Saddle — Black',
        imageUrl: null,
        weightLbs: 0.45,
        upc: '012345678911',
        mpn: 'ARES-R3-BLK',
        itemType: 'paa',
        binLocations: [
          {
            id: 214,
            binCode: 'D4-08-A',
            quantity: 2,
            zone: 'Shelving D',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80052',
        sku: 'PAA-BAR-TAPE-WHT',
        title: 'Fizik Vento Microtex Tacky Bar Tape — White',
        imageUrl: null,
        weightLbs: 0.1,
        upc: '012345678912',
        mpn: null,
        itemType: 'paa',
        binLocations: [
          {
            id: 215,
            binCode: 'A1-02-C',
            quantity: 6,
            zone: 'Shelving A',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
      {
        shopifyLineItemGid: 'gid://shopify/LineItem/80053',
        sku: 'PAA-CO2-CART-16G',
        title: 'Genuine Innovations 16g CO2 Cartridge (3-pack)',
        imageUrl: null,
        weightLbs: 0.25,
        upc: '012345678913',
        mpn: 'GI-CO2-16G-3PK',
        itemType: 'paa',
        binLocations: [
          {
            id: 216,
            binCode: 'A1-02-D',
            quantity: 4,
            zone: 'Shelving A',
            parentZone: 'P&A Pick',
            locationType: 'pick',
          },
        ],
      },
    ],
    hasAddressError: false,
  },
];

export const MOCK_SESSIONS: Record<number, PackSession> = {
  // 8006: single bike — Bike Box pre-added, bike pre-assigned (for deep links to steps B/C/D)
  8006: {
    orderId: 8006,
    boxes: [{ id: 'box-1', size: 'Bike Box - Medium', tareLbs: 28 }],
    labels: {},
    slipsPrinted: {},
    addressConfirmed: false,
    lineStates: {
      'gid://shopify/LineItem/80061': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
    },
  },
  // 8007: 2-box scenario — bike in Bike Box (pre-assigned), accessories unassigned
  8007: {
    orderId: 8007,
    boxes: [
      { id: 'box-1', size: 'Bike Box - Medium', tareLbs: 28 },
      { id: 'box-2', size: 'Small P&A Box', tareLbs: 2 },
    ],
    labels: {},
    slipsPrinted: {},
    addressConfirmed: false,
    lineStates: {
      'gid://shopify/LineItem/80071': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
      'gid://shopify/LineItem/80072': { boxId: null, exceptionType: null, exceptionReason: null },
      'gid://shopify/LineItem/80073': { boxId: null, exceptionType: null, exceptionReason: null },
    },
  },
  8002: {
    orderId: 8002,
    boxes: [{ id: 'box-1', size: 'Small P&A Box', tareLbs: 2 }],
    labels: {},
    slipsPrinted: { 'box-1': true },
    addressConfirmed: false,
    lineStates: {
      'gid://shopify/LineItem/80021': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
      'gid://shopify/LineItem/80022': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
      'gid://shopify/LineItem/80023': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
    },
  },
  8003: {
    orderId: 8003,
    boxes: [
      { id: 'box-1', size: 'Small P&A Box', tareLbs: 2 },
      { id: 'box-2', size: 'Small P&A Box', tareLbs: 2 },
    ],
    labels: {},
    slipsPrinted: {},
    addressConfirmed: false,
    lineStates: {
      'gid://shopify/LineItem/80031': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
      'gid://shopify/LineItem/80032': {
        boxId: 'box-1',
        exceptionType: null,
        exceptionReason: null,
      },
      'gid://shopify/LineItem/80033': { boxId: null, exceptionType: null, exceptionReason: null },
      'gid://shopify/LineItem/80034': { boxId: null, exceptionType: null, exceptionReason: null },
    },
  },
  8005: {
    orderId: 8005,
    boxes: [{ id: 'box-1', size: 'Small P&A Box', tareLbs: 2 }],
    labels: {},
    slipsPrinted: {},
    addressConfirmed: false,
    lineStates: {
      'gid://shopify/LineItem/80051': { boxId: null, exceptionType: null, exceptionReason: null },
      'gid://shopify/LineItem/80052': { boxId: null, exceptionType: null, exceptionReason: null },
      'gid://shopify/LineItem/80053': {
        boxId: null,
        exceptionType: 'item_not_found',
        exceptionReason: 'Could not locate in staging area',
      },
    },
  },
};

export const MOCK_LABEL_PDF_URL =
  'https://deliver.goshippo.com/04239da0a46b480592aa973df7fabdac.pdf?Expires=1808231503&Signature=Y5MulpbjyDgBhzLTvcAjdYmaRc8BaWpmtlVhlp-nEj6-n6pSpFhe5lOhxkwvkU0~fYvkh-0B2cgA-UFzL1eeZJnwKK8UN64g9Excx-8aEDfFwxvBIAFxElSwHvbzmpyFDHsTfzpxmtGFhbYJ3RyyeymDYnP9fBeuSmYRTa9D3ogCbsidkAcHZ2x-9bbu80oRigBhup8ML4QrjPb1jECYHHo5jO0n9GYHLrCmkvRgc05yZqYdkl4aHBDklGmuds8hpr4apy0zwOeB8lIRbCfofwcU40eLRwcZ~QLIn5aiftgY7z7szMA583lVI1O9G8bcjbQWRld~LYhA7hzcYFwFpQ__&Key-Pair-Id=APKAJRICFXQ2S4YUQRSQ';

export const MOCK_SHIPPO_BASE_RATES: Record<
  CarrierService,
  { baseCost: number; perLbCost: number; transitDays: string }
> = {
  'FedEx Home Delivery': { baseCost: 9.85, perLbCost: 0.18, transitDays: '1–5 business days' },
  'FedEx Ground': { baseCost: 7.5, perLbCost: 0.15, transitDays: '2–7 business days' },
  'USPS Priority Mail': { baseCost: 8.45, perLbCost: 0.22, transitDays: '1–3 business days' },
  'USPS Ground Advantage': { baseCost: 5.9, perLbCost: 0.12, transitDays: '2–5 business days' },
};

export const MOCK_QUEUE: PackQueueItem[] = MOCK_ORDERS.map(o => ({
  shopifyOrderId: o.shopifyOrderId,
  shopifyOrderName: o.shopifyOrderName,
  createdAt: o.createdAt,
  lineItemCount: o.lines.length,
  lifecycleStatus: o.lifecycleStatus,
  isPriority: o.isPriority,
  orderType: deriveOrderType(o.lines.map(l => l.itemType)),
}));
